/**
 * M02 L2 YAML 编辑器 · S2 Day 6b · Monaco editor + 乐观锁 + 后端 schema 校验
 *
 * 数据源：
 * - useL2Rules → GET /admin/v1/intent/l2_rules（载入）
 * - usePutL2Rules → PUT /admin/v1/intent/l2_rules（保存 · 携 expected_version）
 *
 * 设计：
 * - Monaco YAML 语法高亮（plain text 模式 · 不引 monaco-yaml 重型依赖 · MVP 够用）
 * - 后端校验是 SSOT：本地不重复实现 schema 校验 · 错误回显由 API 400 详情驱动
 * - 乐观锁：保存时携 expected_version=载入时的 bundle_version；冲突 409 → 提示并保留草稿
 */
import Editor, { type OnChange } from "@monaco-editor/react";
import { useEffect, useState } from "react";

import { SnapshotPanel } from "@/components/shared/snapshot-panel";
import { useL2Rules, usePutL2Rules } from "@/lib/api/intent";

interface ApiErrorBody {
	detail?: string;
}

function extractErrorDetail(err: unknown): string {
	if (typeof err === "object" && err !== null) {
		const maybeAxios = err as { response?: { data?: ApiErrorBody }; message?: string };
		if (maybeAxios.response?.data?.detail) return maybeAxios.response.data.detail;
		if (maybeAxios.message) return maybeAxios.message;
	}
	return String(err);
}

export function L2YamlEditor() {
	const { data, isLoading, isError, error, refetch } = useL2Rules();
	const putMutation = usePutL2Rules();

	const [draft, setDraft] = useState<string>("");
	const [loadedVersion, setLoadedVersion] = useState<string | null>(null);
	const [conflictMsg, setConflictMsg] = useState<string | null>(null);
	const [savedAt, setSavedAt] = useState<string | null>(null);

	useEffect(() => {
		if (data && loadedVersion === null) {
			setDraft(data.yaml_text);
			setLoadedVersion(data.bundle_version);
		}
	}, [data, loadedVersion]);

	const isDirty = data ? draft !== data.yaml_text || loadedVersion !== data.bundle_version : false;

	const handleSave = () => {
		setConflictMsg(null);
		setSavedAt(null);
		putMutation.mutate(
			{ yaml_text: draft, expected_version: loadedVersion },
			{
				onSuccess: (res) => {
					setSavedAt(new Date().toLocaleTimeString());
					setLoadedVersion(res.bundle_version);
				},
				onError: (err) => {
					const detail = extractErrorDetail(err);
					const status = (err as { response?: { status?: number } })?.response?.status;
					if (status === 409) {
						setConflictMsg(
							`乐观锁冲突 · 后端版本已变 · 请点"重载最新版本"再合并草稿。原始错误：${detail}`,
						);
					} else {
						setConflictMsg(`保存失败：${detail}`);
					}
				},
			},
		);
	};

	const handleReload = async () => {
		const result = await refetch();
		if (result.data) {
			setDraft(result.data.yaml_text);
			setLoadedVersion(result.data.bundle_version);
			setConflictMsg(null);
		}
	};

	const handleEditorChange: OnChange = (value) => {
		setDraft(value ?? "");
	};

	if (isLoading) {
		return (
			<div data-testid="l2-editor-loading" className="p-4 text-sm text-text-secondary">
				加载分类规则配置中...
			</div>
		);
	}

	if (isError) {
		return (
			<div data-testid="l2-editor-error" className="p-4 text-sm text-brand-danger">
				加载失败：{error instanceof Error ? error.message : String(error)}
			</div>
		);
	}

	return (
		<div className="space-y-3" data-testid="l2-editor">
			<SnapshotPanel
				module={{ kind: "l2_rules" }}
				moduleLabel="分类规则"
				testidPrefix="l2-snapshot"
			/>
			<div className="flex flex-wrap items-center justify-between gap-2 text-sm">
				<div className="text-text-secondary">
					当前版本 <span className="font-mono text-xs">{loadedVersion ?? "—"}</span>
					<span className="ml-3">
						规则 {data?.enabled_rules ?? 0}/{data?.total_rules ?? 0}
						<span className="ml-1 text-text-muted">启用/总数</span>
					</span>
					{isDirty ? (
						<span className="ml-3 rounded border border-brand-warning/30 bg-brand-warning/10 px-2 py-0.5 text-xs text-brand-warning">
							草稿未保存
						</span>
					) : null}
					{savedAt ? (
						<span
							data-testid="l2-editor-saved-at"
							className="ml-3 rounded border border-brand-success/30 bg-brand-success/10 px-2 py-0.5 text-xs text-brand-success"
						>
							已保存 {savedAt}
						</span>
					) : null}
				</div>
				<div className="flex gap-2">
					<button
						type="button"
						className="rounded-md border border-surface-border px-3 py-1.5 text-sm hover:bg-surface-subtle"
						data-testid="l2-editor-reload"
						onClick={handleReload}
					>
						重载最新版本
					</button>
					<button
						type="button"
						className="rounded-md border border-brand-accent bg-brand-accent px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
						data-testid="l2-editor-save"
						onClick={handleSave}
						disabled={putMutation.isPending || !isDirty}
					>
						{putMutation.isPending ? "保存中..." : "保存"}
					</button>
				</div>
			</div>

			<div className="rounded-md border border-surface-border" data-testid="l2-editor-monaco">
				<Editor
					height="60vh"
					language="yaml"
					value={draft}
					onChange={handleEditorChange}
					options={{
						minimap: { enabled: false },
						fontSize: 13,
						tabSize: 2,
						wordWrap: "on",
						scrollBeyondLastLine: false,
					}}
				/>
			</div>

			{conflictMsg ? (
				<div
					data-testid="l2-editor-conflict"
					className="rounded-md border border-brand-danger bg-brand-danger/10 p-3 text-sm text-brand-danger"
				>
					{conflictMsg}
				</div>
			) : null}

			<p className="text-xs text-text-muted">
				保存时以后端规则校验为准；校验失败会直接显示错误原因。
			</p>
		</div>
	);
}
